import { RequestCooldownService } from './request-cooldown.service';
import { AuthService as AuthApi } from '../../shared/api/generated/auth/auth.service';
import { inject, Injectable, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { error, info, warn } from '@4players/odin-common';
import { recoveryInspectSchema, Role, roleSchema } from '../../shared/schemas/backend-api';

/**
 * State of the recovery-link landing view (`activeSection: 'recover-password'`).
 * `null` = no recovery link is being handled.
 */
export type RecoveryLandingState =
  | { status: 'inspecting'; token: string }
  | { status: 'ready'; token: string; roleName: string; expiresAt: number }
  | { status: 'consuming'; token: string; roleName: string }
  | { status: 'invalid' }
  | { status: 'error'; token: string };

/**
 * State of the "Forgot password?" request. `sandbox`: the server's mail
 * transport only validated the message (Mailjet sandbox) — nothing was
 * delivered. `unavailable`: no transport / recipient / eligible role — an
 * administrator has to reset the password instead.
 */
export type RecoveryRequestState =
  'idle' | 'sending' | 'sent' | 'sandbox' | 'rate-limited' | 'unavailable' | 'error';

/**
 * Role password recovery by e-mail. There are no user accounts — a session
 * signs in to a ROLE by password — so "Forgot password?" asks the server to
 * mail the app's contact address a one-time link (`/#recover=<token>`).
 * Opening it lands on a confirmation view; only pressing Continue redeems the
 * token (a scanner that pre-opens the link must not burn it) and signs this
 * session in as the recovery role, exactly like a login. The user then sets
 * new passwords in Settings → Permissions with the ordinary editor.
 *
 * Endpoints (all under `config.backendUrl`):
 * - `POST /v1/auth/recover/request` → 200 | 429 `{ retryAfter }` | 503 | 502
 * - `POST /v1/auth/recover/inspect` `{ token }` → `{ roleName, expiresAt }` | 404
 * - `POST /v1/auth/recover` `{ token }` → role wire (like login) | 410
 */
@Injectable({ providedIn: 'root' })
export class PasswordRecoveryService {
  readonly cooldown = inject(RequestCooldownService);
  private readonly authApi = inject(AuthApi);

  /** From `GET /v1/startup` — whether the server can send recovery mail for this app. */
  readonly available = signal(false);
  readonly landing = signal<RecoveryLandingState | null>(null);
  readonly requestState = signal<RecoveryRequestState>('idle');
  readonly retryAfterSeconds = signal<number | null>(null);
  /**
   * One-shot marker for the permissions screen after a successful redeem: it
   * pre-selects this role and reminds the user to set a new password. Cleared
   * once a password is saved, on dismiss, or on logout. Never persisted.
   */
  readonly recoveredRole = signal<{ id?: string; name: string } | null>(null);

  get hasPendingLanding(): boolean {
    return this.landing() !== null;
  }

  async requestRecovery(): Promise<void> {
    if (this.requestState() === 'sending' || this.cooldown.remaining('recoveryRequest')) return;
    // Always ask the server, even when startup reported `available: false`:
    // its 503 yields the same notice, and the server log then says WHY
    // (missing transport / recipient / eligible role) — a client-side
    // short-circuit would hide that from whoever is configuring the instance.
    this.requestState.set('sending');
    this.retryAfterSeconds.set(null);
    try {
      const result = await firstValueFrom(this.authApi.requestRecovery());
      if (result.delivered === false) {
        warn('password recovery e-mail was validated but not delivered (mail sandbox)');
        this.requestState.set('sandbox');
        return;
      }
      info('password recovery e-mail requested');
      this.requestState.set('sent');
    } catch (e) {
      if (e instanceof HttpErrorResponse) {
        if (e.status === 429) {
          this.cooldown.capture('recoveryRequest', e);
          const retryAfter = this.cooldown.remaining('recoveryRequest');
          this.retryAfterSeconds.set(Number.isFinite(retryAfter) ? retryAfter : null);
          this.requestState.set('rate-limited');
          return;
        }
        if (
          e.status === 503 &&
          !['RATE_LIMIT_UNAVAILABLE', 'RESOURCE_BUSY'].includes(e.error?.code)
        ) {
          warn('password recovery is unavailable for this app');
          this.requestState.set('unavailable');
          return;
        }
      }
      error('password recovery request failed', { e });
      this.requestState.set('error');
    }
  }

  resetRequestState(): void {
    if (this.cooldown.remaining('recoveryRequest')) return;
    this.requestState.set('idle');
  }

  /** Start handling a recovery link: show the landing view and look the token up. */
  begin(token: string): void {
    this.landing.set({ status: 'inspecting', token });
    void this.inspect(token);
  }

  /** Non-consuming lookup — the token stays valid until `consume()`. */
  async inspect(token: string): Promise<void> {
    if (this.cooldown.remaining('recoveryInspect')) return;
    try {
      const raw = await firstValueFrom(this.authApi.inspectRecovery({ token }));
      const { roleName, expiresAt } = recoveryInspectSchema.parse(raw);
      this.landing.set({ status: 'ready', token, roleName, expiresAt });
    } catch (e) {
      this.cooldown.capture('recoveryInspect', e);
      if (e instanceof HttpErrorResponse && [400, 404, 410].includes(e.status)) {
        warn('recovery link rejected by the server', { status: e.status });
        this.landing.set({ status: 'invalid' });
        return;
      }
      error('failed to inspect recovery link', { e });
      this.landing.set({ status: 'error', token });
    }
  }

  /** Retry a failed inspect (transport error). */
  retry(): void {
    if (this.cooldown.remaining('recoveryInspect')) return;
    const current = this.landing();
    if (current?.status !== 'error') return;
    this.begin(current.token);
  }

  /**
   * Redeem the token. Resolves with the role the session is now signed in to
   * (the caller applies it via `AppService.handlePermissionsUpdate`), or null
   * when the link was invalid / already used / could not be redeemed.
   */
  async consume(): Promise<Role | null> {
    if (this.cooldown.remaining('recoveryRedeem')) return null;
    const current = this.landing();
    if (current?.status !== 'ready') return null;
    const { token, roleName } = current;
    this.landing.set({ status: 'consuming', token, roleName });
    try {
      const raw = await firstValueFrom(this.authApi.consumeRecovery({ token }));
      const role = roleSchema.parse(raw);
      info('recovery link redeemed', { role: role.name });
      this.recoveredRole.set({ id: role.id, name: role.name });
      this.landing.set(null);
      return role;
    } catch (e) {
      if (this.cooldown.capture('recoveryRedeem', e)) {
        this.landing.set({ status: 'ready', token, roleName, expiresAt: current.expiresAt });
        return null;
      }
      if (e instanceof HttpErrorResponse && [400, 404, 410].includes(e.status)) {
        warn('recovery link could not be redeemed', { status: e.status });
        this.landing.set({ status: 'invalid' });
        return null;
      }
      error('failed to redeem recovery link', { e });
      this.landing.set({ status: 'error', token });
      return null;
    }
  }

  /** Leave the landing view without redeeming (the token stays valid until it expires). */
  dismiss(): void {
    this.landing.set(null);
  }

  clearRecoveredRole(): void {
    this.recoveredRole.set(null);
  }
}
